import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { messageService } from "../../services/messageService";

type ApiError = Error & { response?: AxiosResponse };

/**
 * The shared axios interceptor resolves 4xx/5xx with `error.response` instead of rejecting, so a
 * mutation that only awaits the call would treat a rejected send (closed or "No answer" thread,
 * archived patient, ward gate) as a success. Throwing here fails the mutation, skips onSuccess and
 * lets the caller show the error.
 */
export function ensureOk<T>(response: AxiosResponse<T>, action: string): T {
  if (!response || response.status < 200 || response.status >= 300) {
    const err: ApiError = new Error(
      `Message thread ${action} failed with status ${response?.status}`
    );
    err.response = response;
    throw err;
  }
  return response.data;
}

/**
 * Refreshes every view a thread change can reach: the patient-page table and the open thread modal.
 *
 * `refetchType: "all"` is not optional — the app sets `refetchOnMount: false` and a three minute
 * `staleTime` globally (src/config/query.ts), so a plain invalidate marks the data stale and leaves
 * the screen showing the old version. Same reason `invalidateAllNoteCaches` does it.
 */
export function invalidateAllMessageCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: ["patient-messages-infinite"],
    refetchType: "all",
  });
  queryClient.invalidateQueries({
    queryKey: ["message-thread"],
    refetchType: "all",
  });
}

/** Sends into an existing thread. The API reopens a closed one in the same operation. */
export function useSendThreadMessage() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      threadId,
      body,
    }: {
      threadId: string;
      body: string;
    }) => {
      return ensureOk(
        await messageService.sendThreadMessage(threadId, body),
        "send"
      );
    },
    onSuccess: () => invalidateAllMessageCaches(queryClient),
  });

  return { mutateAsync, isPending };
}

/** Marks the conversation read for staff, so the table row loses its highlight and badge straight away. */
export function useMarkThreadRead() {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: async (threadId: string) => {
      return ensureOk(await messageService.markThreadRead(threadId), "read");
    },
    onSuccess: () => invalidateAllMessageCaches(queryClient),
  });

  return { mutate };
}

export function useCloseThread() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (threadId: string) => {
      return ensureOk(await messageService.closeThread(threadId), "close");
    },
    onSuccess: () => invalidateAllMessageCaches(queryClient),
  });

  return { mutateAsync, isPending };
}
